import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOHead from "@/components/feature/SEOHead";
import { categoryList, getArticlesByCategory } from "@/mocks/categoryData";

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug } = useParams<{ slug?: string }>();
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const tabParam = searchParams.get("tab") || slug || "all";
    if (categoryList.some((c) => c.slug === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams, slug]);

  const articles = useMemo(() => getArticlesByCategory(activeTab), [activeTab]);
  const activeCategory = categoryList.find((c) => c.slug === activeTab);

  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    setSearchParams({ tab: tabSlug });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: "赛事资讯", path: "/category" },
    ...(activeCategory && activeCategory.slug !== "all"
      ? [{ label: activeCategory.name }]
      : []),
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="赛事资讯 - 开云体育 | 最新体育新闻与赛事分析"
        description="开云体育赛事资讯频道，为您提供最新最全面的体育新闻、赛事分析、投注技巧及行业动态。涵盖足球、篮球、网球、电竞等热门项目。"
        keywords="体育资讯,赛事分析,开云体育,体育新闻,足球新闻,篮球新闻,电竞新闻"
        canonicalUrl="https://mdsygs.cn/category"
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              <a href="/category">赛事资讯</a>
            </h1>
            <p className="text-sm text-foreground-600">
              最新体育赛事动态、专业分析与投注指南
            </p>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categoryList.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleTabChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeTab === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden cursor-pointer hover:border-primary-300 transition-all duration-300"
              >
                <div className="relative w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                    {article.summary}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{article.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {articles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}